import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TokenService } from './token.service';
import { environment } from '@env/environment';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import { EnrollmentData } from '../models/enroll-user.models';
import { TranslateService } from '@ngx-translate/core';


// Modelo para la respuesta genérica de la API
export interface ApiResponse<T> {
  status: boolean;
  data: T;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class EnrollUserService {

  private tokenService = inject(TokenService);
  private http = inject(HttpClient);
  private translate = inject(TranslateService);


  /**
  * ✅ Crea las cabeceras de autenticación para las peticiones.
  */
  private getAuthHeaders(): { headers: HttpHeaders } {
    const token = this.tokenService.getToken();
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
    return { headers };
  }

  initEnroll(courseID: string) {
    this.enrollUserToCourse(courseID).subscribe({
      next: (data: any) => {

        sessionStorage.removeItem('courseIDToEnroll');
        const config = data?.extra_data?.config_redirect;
        if (!config) {
          // Si la estructura de datos no es la esperada, maneja el error
          console.error('La estructura de datos de la API no es la esperada.', data);
          window.location.href = `${environment.mainApp}/home`;
          return;
        }

        const courseToRedirect = {
          ...config,
          state: config.certification_state
        };

        //Se realizó la inscripción al curso, ahora debe redirigir al classroom
        const urlToClassroom = `${environment.mainApp}/classroom?config=${JSON.stringify(courseToRedirect)}`;
        window.location.href = urlToClassroom;
      },
      error: (error) => {
        console.error('Error al inscribir el curso:', error);
        //Si falla la inscripción al curso, redirigir solo al home
        window.location.href = `${environment.mainApp}/home`;
      }
    });
  }

  updateAccessToken() {
    const { id } = this.tokenService.getDecodedToken();
    /*
    obtener token decodeado
    encodear token
    y almacenarlo en las cookies
    */
  }





  /**
     * ✅ Inscribe un usuario a un curso y actualiza sus datos.
     * Aplanamos toda la lógica anidada en un solo pipe de RxJS.
     * Devuelve el ID de la nueva certificación.
     * @param courseId El ID del curso al que se va a inscribir.
     */
  // ATENCIÓN: Se debe cambiar el tipo de retorno para que coincida con lo que quieres devolver.
  // Ya no es Observable<string>, ahora es el tipo de la respuesta del post.
  enrollUserToCourse(courseId: string): Observable<EnrollmentData> {
    const { id } = this.tokenService.getDecodedToken();
    const url = `${environment.apiEndpoint}/certifications`;
    const body = {
      data: {
        user_id: id,
        curso_id: courseId
      }
    };

    return this.http.post<ApiResponse<EnrollmentData>>(url, body, this.getAuthHeaders()).pipe(
      map((apiResponse) => {

        // 3. Hacemos las validaciones sobre el objeto `apiResponse` correcto.
        if (!apiResponse.status || !apiResponse.data) {
          // Si la API dice que falló, lanzamos un error que será atrapado por catchError
          throw new Error(apiResponse.errorMessage || this.translate.instant('auth.enroll.errors.couldNotEnrollCourse'));
        }

        // Opcional: puedes hacer validaciones sobre los datos internos también.
        const certificationId = apiResponse.data.extra_data?.config_redirect?.certification_id;
        if (!certificationId) {
          throw new Error(this.translate.instant('auth.enroll.errors.missingCertificationId'));
        }

        sessionStorage.removeItem('courseIDToEnroll');

        //Actualizamos en ultimo curso (certification_id) visto por el usuario
        const updateUserBody = { data: { certification_id: certificationId } };
        this.updateUser(updateUserBody).subscribe();

        return apiResponse.data;
      }),
      catchError(error => {
        // Es una buena práctica añadir un catchError para manejar los errores lanzados.
        console.error('Error en enrollUserToCourse:', error);
        // Puedes relanzar el error o devolver un valor por defecto.
        throw error;
      })
    );
  }

  /**
   * ✅ Efecto secundario: Actualiza datos locales.
   * Separado en una función privada para mayor claridad.
   */
  private updateLocalUserData(certificationId: string): void {
    // Usamos 'tap' en el futuro si queremos integrar esto en el pipe,
    // pero como función privada es más legible.

    // if (this.appService.user_data?.certification_id === '0') {
    //   localStorage.setItem('last-certificate-default', certificationId);
    //   this.appService.user_data.certification_id = certificationId;
    // }
  }

  /**
   * ✅ Manejador de errores centralizado.
   */
  private handleError(error: HttpErrorResponse | Error): Observable<never> {
    let errorMessage = this.translate.instant('auth.serverErrors.default');
    if (error instanceof Error) {
      // Captura errores lanzados manualmente (ej: throw new Error(...))
      errorMessage = error.message;
    } else {
      // Captura errores de HttpClient (ej: 404, 500)
      errorMessage = this.translate.instant('auth.enroll.errors.httpError', {
        status: error.status,
        message: error.message,
      });
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }



  /**
   * ✅ Actualiza los datos de un usuario.
   * Devuelve un Observable con los datos del usuario actualizado.
   * @param payload Un objeto con las propiedades del usuario a actualizar.
   */
  updateUser(payload: Partial<any>): Observable<any> {
    const { id } = this.tokenService.getDecodedToken();

    const url = `${environment.apiEndpoint}/users/${id}`;

    // Nota: El método `put` debería incluir las cabeceras de autenticación
    return this.http.put<ApiResponse<any>>(url, payload, this.getAuthHeaders()).pipe(
      map(response => {
        // 1. Valida la respuesta de la API
        if (!response?.status) {
          // 2. Si falla, lanza un error que será atrapado por catchError
          throw new Error(response.errorMessage || this.translate.instant('auth.enroll.errors.invalidApiResponse'));
        }
        // 3. Si tiene éxito, devuelve solo los datos del usuario, que es lo que nos interesa
        return response.data;
      }),
      // 4. Atrapa errores de red o los que lanzamos manualmente
      catchError(this.handleError)
    );
  }


}
